import { useCallback, useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { useAlert } from '../contexts/AlertContext'
import { useAuth } from '../hooks/useAuth'
import {
  attendanceGetAccess,
  attendanceLeaderClosePeriod,
  attendanceLeaderCopyMonthPlan,
  attendanceLeaderCreatePunch,
  attendanceLeaderDeletePunch,
  attendanceLeaderGeneratePlan,
  attendanceLeaderLoadEmployees,
  attendanceLeaderLoadPrehled,
  attendanceLeaderOverview,
  attendanceLeaderSavePrehledDay,
  attendanceLeaderUpdatePunch,
  attendancePersonalistaCopyMonthPlan,
  attendancePersonalistaCreatePunch,
  attendancePersonalistaGeneratePlan,
  attendancePersonalistaLoadEmployees,
  attendancePersonalistaLoadPrehled,
  attendancePersonalistaOverview,
  attendancePersonalistaPeriodAction,
  attendancePersonalistaSavePrehledDay,
  downloadAttendancePrehledPdf,
  downloadAttendancePayrollCsv,
  attendancePersonalistaBankAdjust,
  type AttendanceHrOverviewRow,
  type AttendanceLeaderEmployeeBrief,
  type AttendanceLeaderPrehled,
  type AttendancePrehledDay,
  type AttendancePunch,
  type AttendancePunchType,
} from '../lib/apiPhpAdapter'
import { formatDisplayNameFromString, formatPersonListColumns } from '../utils/employeeDisplay'
import { ATT_HR_SECTIONS } from './attendance/AttendanceHrExtrasPanel'
import './AttendanceLeaderView.css'
import './AttendanceView.css'
import './attendance/AttendanceOrgAdmin.css'

export type AttendancePrehledMode = 'leader' | 'personalista'

const PUNCH_TYPES: Array<{ type: AttendancePunchType; label: string }> = [
  { type: 'arrival', label: 'Příchod' },
  { type: 'departure', label: 'Odchod' },
  { type: 'departure_indirect', label: 'Odchod na nepřímou činnost' },
  { type: 'arrival_indirect', label: 'Příchod z nepřímé činnosti' },
  { type: 'departure_doctor', label: 'Odchod k lékaři' },
  { type: 'arrival_doctor', label: 'Příchod od lékaře' },
]

type RightTab = 'nepritomnosti' | 'smeny' | 'soucet'

/** Co zapsat na vybrané dny (bez „štětečku“ — nejdřív výběr dne, pak kód). */
type DayAssignMode =
  | { kind: 'absence'; id: number; label: string }
  | { kind: 'shift'; id: number; label: string }
  | { kind: 'clear' }

type EditState = {
  mode: 'create' | 'edit'
  punchId?: number
  type: AttendancePunchType
  punchedAtLocal: string
}

type ConfirmDialog =
  | { kind: 'close_one' }
  | { kind: 'close_all' }
  | { kind: 'copy_month' }
  | { kind: 'delete_punch'; label: string }

type GeneratePlanDialog = { overwrite: boolean }

type BankAdjustDialog = { delta: string; note: string }

type CloseChecklistItem = { level: 'warn' | 'info'; text: string }

const CODE_SWATCHES = [
  '#22c55e',
  '#ef4444',
  '#3b82f6',
  '#a855f7',
  '#f97316',
  '#92400e',
  '#06b6d4',
  '#eab308',
  '#ec4899',
  '#64748b',
  '#14b8a6',
  '#dc2626',
]

function formatPunchClock(iso: string): string {
  const m = String(iso).match(/[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  return m ? `${m[1]}:${m[2]}` : iso
}

function punchDayNumber(iso: string): number | null {
  const m = String(iso).match(/^\d{4}-\d{2}-(\d{2})/)
  return m ? parseInt(m[1], 10) : null
}

function punchEpoch(iso: string): number {
  const t = Date.parse(iso.includes('T') ? iso : iso.replace(' ', 'T'))
  return Number.isFinite(t) ? t : 0
}

function formatHm(totalMinutes: number): string {
  const abs = Math.abs(Math.round(totalMinutes))
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

function toDatetimeLocalValue(iso: string): string {
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/)
  if (m) return `${m[1]}T${m[2]}`
  return nowDatetimeLocal()
}

function nowDatetimeLocal(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function datetimeLocalForDay(year: number, month: number, day: number): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const now = new Date()
  const isToday =
    now.getFullYear() === year && now.getMonth() + 1 === month && now.getDate() === day
  const hh = isToday ? pad(now.getHours()) : '08'
  const mm = isToday ? pad(now.getMinutes()) : '00'
  return `${year}-${pad(month)}-${pad(day)}T${hh}:${mm}`
}

function monthTitle(year: number, month: number): string {
  return `${String(month).padStart(2, '0')}/${year}`
}

function dayCodeLabel(day: AttendancePrehledDay): string {
  if (day.absence?.label) return day.absence.label
  if (day.absence?.code) return day.absence.code
  if (day.shift?.label) return day.shift.label
  if (day.shift?.name) return day.shift.name.slice(0, 4)
  return ''
}

/** Odprac. — jen skutečné punch-minuty z API (`hours_label` / `minutes`). */
function dayOdpracLabel(day: AttendancePrehledDay): string {
  if (day.minutes <= 0 && !day.open) return ''
  return `${day.hours_label}${day.open ? '…' : ''}`
}

/**
 * Neodprac. — jen u nepřítomnosti.
 * Částečná: `absence.hours`; celodenní: API nuluje normu → použij default směny.
 */
function dayNeodpracLabel(day: AttendancePrehledDay, defaultNormMinutes: number): string {
  if (!day.absence) return ''
  const hours = day.absence.hours
  if (hours != null && hours > 0 && hours < 8) {
    return formatHm(Math.round(hours * 60))
  }
  if (day.norm_minutes > 0) return day.norm_hours_label
  const fallback = defaultNormMinutes > 0 ? defaultNormMinutes : 8 * 60
  return formatHm(fallback)
}

/** Rozdíl = odpracované minuty − norma dne (stejná logika jako měsíční saldo). */
function dayRozdilLabel(day: AttendancePrehledDay): string {
  const diff = day.minutes - day.norm_minutes
  if (day.minutes === 0 && day.norm_minutes === 0) return ''
  if (diff === 0) return '0:00'
  return diff < 0 ? `-${formatHm(Math.abs(diff))}` : formatHm(diff)
}

function dayNormaLabel(day: AttendancePrehledDay): string {
  if (day.norm_minutes <= 0) return ''
  return day.norm_hours_label
}

function defaultShiftNormMinutes(prehled: AttendanceLeaderPrehled | null): number {
  const s = prehled?.default_shift
  if (!s?.time_from || !s?.time_to) return 8 * 60
  const parse = (t: string) => {
    const m = t.match(/^(\d{1,2}):(\d{2})/)
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null
  }
  const from = parse(s.time_from)
  const to = parse(s.time_to)
  if (from == null || to == null) return 8 * 60
  let span = to - from
  if (span <= 0) span += 24 * 60
  const brk = typeof s.break_minutes === 'number' ? s.break_minutes : 0
  return Math.max(0, span - brk)
}

function codeSwatch(id: number): string {
  return CODE_SWATCHES[Math.abs(id) % CODE_SWATCHES.length]
}

const AUTO_PAUSE_COLORS: Record<string, string> = {
  break: '#16a34a',
}

type LogRow = {
  key: string
  punch: AttendancePunch | null
  auto: boolean
  autoKind?: 'break'
  day: number | null
  punchedAt: string
  label: string
  color: string | null
  duration: string
  shiftLabel: string
  dayStart: boolean
}

function punchColorForType(
  type: string,
  catalog?: Array<{ code: string; color?: string | null }>,
): string | null {
  const fromCat = catalog?.find((t) => t.code === type)?.color
  if (fromCat) return fromCat
  if (type.startsWith('arrival')) return '#2563eb'
  if (type.startsWith('departure')) return '#dc2626'
  return null
}

function isoForDayClock(dayDate: string, hhmm: string): string {
  const t = hhmm.length >= 5 ? hhmm.slice(0, 5) : hhmm
  return `${dayDate}T${t}:00`
}

export default function AttendanceLeaderView({
  mode = 'leader',
}: {
  mode?: AttendancePrehledMode
}) {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { showAlert } = useAlert()
  const [gateOk, setGateOk] = useState<boolean | null>(null)
  const isHr = mode === 'personalista'
  /** Personalista: 3-pane detail otevřený v modalu po kliknutí na řádek přehledu. */
  const [detailOpen, setDetailOpen] = useState(false)
  const [hrOverview, setHrOverview] = useState<AttendanceHrOverviewRow[]>([])
  const [loadingHrOverview, setLoadingHrOverview] = useState(false)
  const [overviewSearch, setOverviewSearch] = useState('')
  const [overviewStatus, setOverviewStatus] = useState<'all' | 'open' | 'closed'>('all')

  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [employees, setEmployees] = useState<AttendanceLeaderEmployeeBrief[]>([])
  const [employeeIndex, setEmployeeIndex] = useState(0)
  const [prehled, setPrehled] = useState<AttendanceLeaderPrehled | null>(null)
  /** Uzavřený měsíc dle overview API (employee_id → closed) */
  const [closedByEmployee, setClosedByEmployee] = useState<Record<number, boolean>>({})
  const [selectedDay, setSelectedDay] = useState<number | null>(null)
  const [selectedPunchId, setSelectedPunchId] = useState<number | null>(null)
  const [rightTab, setRightTab] = useState<RightTab>('nepritomnosti')
  /** Začátek rozsahu (Shift+klik); bez Shift jen jeden vybraný den. */
  const [rangeAnchor, setRangeAnchor] = useState<number | null>(null)
  const [loadingList, setLoadingList] = useState(true)
  const [loadingPrehled, setLoadingPrehled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [edit, setEdit] = useState<EditState | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null)
  const [generatePlanDialog, setGeneratePlanDialog] = useState<GeneratePlanDialog | null>(null)
  const [bankDialog, setBankDialog] = useState<BankAdjustDialog | null>(null)
  const [closeChecklist, setCloseChecklist] = useState<{
    scope: 'one' | 'all'
    items: CloseChecklistItem[]
  } | null>(null)

  const selectedEmployee = employees[employeeIndex] ?? null
  const employeeId = selectedEmployee?.employee_id ?? null

  /** Výběr zaměstnance (HR sestavy i levý seznam workspace). */
  const selectEmployee = (id: number) => {
    const idx = employees.findIndex((x) => x.employee_id === id)
    if (idx < 0) return
    setEmployeeIndex(idx)
    setRangeAnchor(null)
    setSelectedPunchId(null)
    setEdit(null)
  }

  const openEmployeeDetail = (id: number) => {
    selectEmployee(id)
    if (isHr) setDetailOpen(true)
  }

  const buildCloseChecklist = useCallback((): CloseChecklistItem[] => {
    if (!prehled) return []
    const items: CloseChecklistItem[] = []
    for (const day of prehled.days) {
      if (day.weekend) continue
      if (day.open) {
        items.push({
          level: 'warn',
          text: `${day.day}. ${day.weekday_short}: otevřený pár příchod/odchod`,
        })
      }
      if (day.shift && day.punch_count === 0 && !day.absence) {
        items.push({
          level: 'warn',
          text: `${day.day}. ${day.weekday_short}: směna bez událostí`,
        })
      }
      if (day.shift && day.absence) {
        items.push({
          level: 'info',
          text: `${day.day}. ${day.weekday_short}: konflikt (směna + nepřítomnost)`,
        })
      }
      if (!day.weekend && !day.shift && !day.absence && day.punch_count > 0) {
        items.push({
          level: 'info',
          text: `${day.day}. ${day.weekday_short}: události bez kódu`,
        })
      }
    }
    return items
  }, [prehled])

  useEffect(() => {
    if (authLoading || !user) return
    let cancelled = false
    attendanceGetAccess()
      .then((a) => {
        if (cancelled) return
        const ok = isHr ? !!a.canPersonalista : !!a.canLeader
        if (!ok) {
          setGateOk(false)
          navigate('/attendance', { replace: true })
          return
        }
        setGateOk(true)
      })
      .catch(() => {
        if (!cancelled) navigate('/attendance', { replace: true })
      })
    return () => {
      cancelled = true
    }
  }, [authLoading, user, navigate, isHr])

  const reloadEmployees = useCallback(async () => {
    setLoadingList(true)
    try {
      const list = isHr
        ? await attendancePersonalistaLoadEmployees()
        : await attendanceLeaderLoadEmployees()
      setEmployees(list)
      setEmployeeIndex((idx) => {
        if (list.length === 0) return 0
        return Math.min(idx, list.length - 1)
      })
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst zaměstnance', 'error')
    } finally {
      setLoadingList(false)
    }
  }, [showAlert, isHr])

  useEffect(() => {
    if (!gateOk) return
    void reloadEmployees()
  }, [gateOk, reloadEmployees])

  const reloadPrehled = useCallback(async () => {
    if (employeeId == null) {
      setPrehled(null)
      return
    }
    setLoadingPrehled(true)
    try {
      const data = isHr
        ? await attendancePersonalistaLoadPrehled(employeeId, year, month)
        : await attendanceLeaderLoadPrehled(employeeId, year, month)
      setPrehled(data)
      setSelectedPunchId(null)
      setEdit(null)
      setSelectedDay((prev) => {
        if (prev != null && prev >= 1 && prev <= data.days_in_month) return prev
        const today =
          now.getFullYear() === year && now.getMonth() + 1 === month ? now.getDate() : 1
        return Math.min(today, data.days_in_month)
      })
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst přehled', 'error')
      setPrehled(null)
    } finally {
      setLoadingPrehled(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refresh when identity/period changes
  }, [employeeId, year, month, showAlert, isHr])

  const reloadClosedFlags = useCallback(async () => {
    try {
      if (isHr) {
        setLoadingHrOverview(true)
        const r = await attendancePersonalistaOverview(year, month)
        const list = Array.isArray(r.employees) ? r.employees : []
        const map: Record<number, boolean> = {}
        for (const e of list) {
          map[e.employee_id] = !!e.closed
        }
        setHrOverview(list)
        setClosedByEmployee(map)
      } else {
        const r = await attendanceLeaderOverview(year, month)
        const map: Record<number, boolean> = {}
        for (const e of r.subordinates || []) {
          map[e.employee_id] = !!e.closed
        }
        setClosedByEmployee(map)
      }
    } catch {
      setClosedByEmployee({})
      if (isHr) setHrOverview([])
    } finally {
      if (isHr) setLoadingHrOverview(false)
    }
  }, [isHr, year, month])

  const closeEmployeeDetail = () => {
    setDetailOpen(false)
    setEdit(null)
    setSelectedPunchId(null)
    void reloadClosedFlags()
  }

  useEffect(() => {
    if (!gateOk || employeeId == null) return
    if (isHr && !detailOpen) return
    void reloadPrehled()
  }, [gateOk, employeeId, reloadPrehled, isHr, detailOpen])

  useEffect(() => {
    if (!gateOk) return
    void reloadClosedFlags()
  }, [gateOk, reloadClosedFlags])

  useEffect(() => {
    if (employeeId == null || !prehled) return
    setClosedByEmployee((prev) => ({ ...prev, [employeeId]: !!prehled.closed }))
  }, [employeeId, prehled])

  const selectedDayRow = useMemo(() => {
    if (!prehled || selectedDay == null) return null
    return prehled.days.find((d) => d.day === selectedDay) ?? null
  }, [prehled, selectedDay])

  const filteredHrOverview = useMemo(() => {
    const q = overviewSearch.trim().toLowerCase()
    return hrOverview.filter((e) => {
      if (overviewStatus === 'open' && e.closed) return false
      if (overviewStatus === 'closed' && !e.closed) return false
      if (!q) return true
      const name = formatDisplayNameFromString(e.name).toLowerCase()
      const pn = (e.personal_number || '').toLowerCase()
      return name.includes(q) || pn.includes(q)
    })
  }, [hrOverview, overviewSearch, overviewStatus])

  const monthPunches = useMemo(() => {
    if (!prehled) return [] as AttendancePunch[]
    return [...prehled.punches].sort((a, b) => punchEpoch(a.punched_at) - punchEpoch(b.punched_at))
  }, [prehled])

  const eventTypeColors = useMemo(() => {
    return prehled?.catalog?.event_types ?? []
  }, [prehled])

  /** Žurnál jako Alfa: punchy + auto přestávka ze směny, čára mezi dny. */
  const punchLogRows = useMemo((): LogRow[] => {
    if (!prehled) return []
    type Raw = {
      key: string
      punch: AttendancePunch | null
      auto: boolean
      autoKind?: 'break'
      day: number | null
      punchedAt: string
      label: string
      color: string | null
      shiftLabel: string
    }
    const raw: Raw[] = []

    for (const p of monthPunches) {
      const day = punchDayNumber(p.punched_at)
      raw.push({
        key: `p-${p.id}`,
        punch: p,
        auto: false,
        day,
        punchedAt: p.punched_at,
        label: p.punch_type_label || p.punch_type,
        color: punchColorForType(p.punch_type, eventTypeColors),
        shiftLabel:
          day != null
            ? dayCodeLabel(prehled.days.find((d) => d.day === day) ?? ({} as AttendancePrehledDay))
            : '',
      })
    }

    for (const day of prehled.days) {
      if (day.weekend || day.holiday || day.absence) continue
      const shift = day.shift
      if (!shift) continue
      // Alfa: přestávka jen když člověk opravdu byl v práci a overlap s oknem pauzy
      // (ne na den bez příchodu / bez odpracovaných minut).
      if ((day.break_deducted_minutes ?? 0) <= 0) continue
      if (!(shift.break_from && shift.break_to)) continue
      const code = dayCodeLabel(day)
      raw.push({
        key: `auto-break-${day.day_date}`,
        punch: null,
        auto: true,
        autoKind: 'break',
        day: day.day,
        punchedAt: isoForDayClock(day.day_date, shift.break_from),
        label: 'Přestávka',
        color: AUTO_PAUSE_COLORS.break,
        shiftLabel: code,
      })
    }

    raw.sort((a, b) => {
      const ea = punchEpoch(a.punchedAt)
      const eb = punchEpoch(b.punchedAt)
      if (ea !== eb) return ea - eb
      if (a.auto !== b.auto) return a.auto ? 1 : -1
      return a.key.localeCompare(b.key)
    })

    return raw.map((r, i) => {
      const next = raw[i + 1]
      let duration = ''
      if (r.auto && r.autoKind === 'break') {
        const d = prehled.days.find((x) => x.day === r.day)
        const deducted = d?.break_deducted_minutes ?? 0
        if (deducted > 0) {
          duration = formatHm(deducted)
        } else if (d?.shift?.break_from && d.shift.break_to) {
          const [fh, fm] = d.shift.break_from.split(':').map(Number)
          const [th, tm] = d.shift.break_to.split(':').map(Number)
          let mins = th * 60 + tm - (fh * 60 + fm)
          if (mins < 0) mins += 24 * 60
          if (mins > 0) duration = formatHm(mins)
        }
      } else if (next && r.day != null && next.day === r.day) {
        const diffMin = Math.round((punchEpoch(next.punchedAt) - punchEpoch(r.punchedAt)) / 60000)
        if (diffMin > 0) duration = formatHm(diffMin)
      }
      const prev = raw[i - 1]
      const dayStart = r.day != null && (i === 0 || prev?.day !== r.day)
      return {
        key: r.key,
        punch: r.punch,
        auto: r.auto,
        autoKind: r.autoKind,
        day: r.day,
        punchedAt: r.punchedAt,
        label: r.label,
        color: r.color,
        duration,
        shiftLabel: r.shiftLabel,
        dayStart,
      }
    })
  }, [monthPunches, prehled, eventTypeColors])

  const selectedPunch = useMemo(
    () => monthPunches.find((p) => p.id === selectedPunchId) ?? null,
    [monthPunches, selectedPunchId],
  )

  const shiftMonth = (delta: number) => {
    let m = month + delta
    let y = year
    if (m < 1) {
      m = 12
      y -= 1
    } else if (m > 12) {
      m = 1
      y += 1
    }
    setYear(y)
    setMonth(m)
    setRangeAnchor(null)
  }

  const shiftYear = (delta: number) => {
    setYear((y) => y + delta)
    setRangeAnchor(null)
  }

  const openCreate = (type: AttendancePunchType = 'arrival') => {
    if (prehled?.closed) {
      showAlert('Období je uzavřeno.', 'error')
      return
    }
    if (!prehled?.has_user_account) {
      showAlert('Podřízený nemá propojený intranet účet — záznam nelze založit.', 'error')
      return
    }
    const day = selectedDay ?? 1
    setEdit({
      mode: 'create',
      type,
      punchedAtLocal: datetimeLocalForDay(year, month, day),
    })
  }

  const openEditPunch = (p: AttendancePunch | null) => {
    if (!p) {
      showAlert('Nejdřív vyberte událost v seznamu.', 'info')
      return
    }
    if (!prehled?.has_user_account) {
      showAlert('Podřízený nemá propojený intranet účet.', 'error')
      return
    }
    setSelectedPunchId(p.id)
    const d = punchDayNumber(p.punched_at)
    if (d != null) setSelectedDay(d)
    setEdit({
      mode: 'edit',
      punchId: p.id,
      type: p.punch_type,
      punchedAtLocal: toDatetimeLocalValue(p.punched_at),
    })
  }

  const saveEdit = async () => {
    if (!edit || employeeId == null) return
    setBusy(true)
    try {
      const punchedAt = edit.punchedAtLocal.replace('T', ' ')
      if (edit.mode === 'create') {
        if (isHr) {
          await attendancePersonalistaCreatePunch(employeeId, {
            type: edit.type,
            punched_at: punchedAt,
          })
        } else {
          await attendanceLeaderCreatePunch(employeeId, {
            type: edit.type,
            punched_at: punchedAt,
          })
        }
        showAlert('Událost přidána (ručně)', 'success')
      } else if (edit.punchId != null) {
        await attendanceLeaderUpdatePunch(edit.punchId, {
          type: edit.type,
          punched_at: punchedAt,
        })
        showAlert('Událost upravena (ručně)', 'success')
      }
      const dayFromEdit = punchDayNumber(punchedAt)
      if (dayFromEdit != null) setSelectedDay(dayFromEdit)
      setEdit(null)
      await reloadPrehled()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const removeSelected = async () => {
    if (!selectedPunch) {
      showAlert('Nejdřív vyberte událost v seznamu.', 'info')
      return
    }
    setConfirmDialog({
      kind: 'delete_punch',
      label: `„${selectedPunch.punch_type_label}“ v ${formatPunchClock(selectedPunch.punched_at)}`,
    })
  }

  const confirmDeletePunch = async () => {
    if (!selectedPunch) {
      setConfirmDialog(null)
      return
    }
    setBusy(true)
    try {
      await attendanceLeaderDeletePunch(selectedPunch.id)
      showAlert('Událost smazána', 'success')
      setSelectedPunchId(null)
      await reloadPrehled()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Smazání selhalo', 'error')
    } finally {
      setBusy(false)
      setConfirmDialog(null)
    }
  }

  const rangeBounds = useMemo(() => {
    if (rangeAnchor == null || selectedDay == null || rangeAnchor === selectedDay) return null
    return {
      from: Math.min(rangeAnchor, selectedDay),
      to: Math.max(rangeAnchor, selectedDay),
    }
  }, [rangeAnchor, selectedDay])

  const selectedDays = useMemo(() => {
    if (!prehled || selectedDay == null) return [] as AttendancePrehledDay[]
    if (rangeBounds) {
      return prehled.days.filter((d) => d.day >= rangeBounds.from && d.day <= rangeBounds.to)
    }
    return prehled.days.filter((d) => d.day === selectedDay)
  }, [prehled, selectedDay, rangeBounds])

  const applyToSelectedDays = async (mode: DayAssignMode) => {
    if (employeeId == null || !prehled) return
    if (selectedDays.length === 0) {
      showAlert('Nejdřív vyberte den ve střední tabulce.', 'info')
      return
    }
    if (prehled.closed) {
      showAlert('Období je uzavřeno.', 'error')
      return
    }
    const targets = mode.kind === 'shift' ? selectedDays.filter((d) => !d.weekend) : selectedDays
    if (targets.length === 0) {
      showAlert(
        mode.kind === 'shift' ? 'Ve výběru nejsou pracovní dny ke směně.' : 'Ve výběru nejsou dny k úpravě.',
        'info',
      )
      return
    }
    setBusy(true)
    try {
      const saveDay = isHr ? attendancePersonalistaSavePrehledDay : attendanceLeaderSavePrehledDay
      let ok = 0
      let fail = 0
      for (const day of targets) {
        try {
          if (mode.kind === 'clear') {
            await saveDay({
              employee_id: employeeId,
              day_date: day.day_date,
              clear: true,
            })
          } else if (mode.kind === 'absence') {
            await saveDay({
              employee_id: employeeId,
              day_date: day.day_date,
              absence_reason_id: mode.id,
              absence_hours: null,
              absence_start_hour: null,
              shift_id: null,
            })
          } else {
            await saveDay({
              employee_id: employeeId,
              day_date: day.day_date,
              shift_id: mode.id,
              absence_reason_id: null,
              absence_hours: null,
              absence_start_hour: null,
            })
          }
          ok++
        } catch {
          fail++
        }
      }
      await reloadPrehled()
      const verb = mode.kind === 'clear' ? 'Smazáno' : 'Uloženo'
      showAlert(
        fail ? `${verb} ${ok} dnů, selhalo ${fail}` : `${verb} ${ok} ${ok === 1 ? 'den' : 'dnů'}`,
        fail ? 'warning' : 'success',
      )
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setBusy(false)
    }
  }

  const clearSelectedCodes = async () => {
    await applyToSelectedDays({ kind: 'clear' })
  }

  const onDayClick = (day: AttendancePrehledDay, e: ReactMouseEvent) => {
    setSelectedPunchId(null)
    if (e.shiftKey && selectedDay != null) {
      setRangeAnchor((prev) => prev ?? selectedDay)
      setSelectedDay(day.day)
      return
    }
    setRangeAnchor(null)
    setSelectedDay(day.day)
  }

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return
      if (rangeAnchor != null) {
        setRangeAnchor(null)
        return
      }
      if (isHr && detailOpen && !edit && !confirmDialog && !bankDialog && !generatePlanDialog && !closeChecklist) {
        closeEmployeeDetail()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [
    rangeAnchor,
    isHr,
    detailOpen,
    edit,
    confirmDialog,
    bankDialog,
    generatePlanDialog,
    closeChecklist,
  ])

  const runClosePeriod = async (scope: 'one' | 'all') => {
    if (scope === 'one' && employeeId == null) return
    setBusy(true)
    try {
      if (scope === 'all') {
        const res = await attendancePersonalistaPeriodAction('close', {
          all: true,
          year,
          month,
        })
        showAlert(`Uzavřeno: ${res.count} zaměstnanců`, 'success')
      } else if (employeeId != null) {
        if (isHr) {
          await attendancePersonalistaPeriodAction('close', {
            employee_id: employeeId,
            year,
            month,
          })
        } else {
          await attendanceLeaderClosePeriod({ employee_id: employeeId, year, month })
        }
        showAlert('Období uzavřeno', 'success')
      }
      await reloadPrehled()
      await reloadClosedFlags()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uzavření selhalo', 'error')
    } finally {
      setBusy(false)
      setCloseChecklist(null)
      setConfirmDialog(null)
    }
  }

  const runGeneratePlan = async (overwrite: boolean) => {
    if (employeeId == null) return
    setBusy(true)
    try {
      if (isHr) {
        const res = await attendancePersonalistaGeneratePlan({
          employee_id: employeeId,
          year,
          month,
          overwrite,
        })
        showAlert(`Směny: zapsáno ${res.written ?? 0}, přeskočeno ${res.skipped ?? 0}`, 'success')
      } else {
        const res = await attendanceLeaderGeneratePlan({
          employee_id: employeeId,
          year,
          month,
          overwrite,
        })
        showAlert(`Směny: zapsáno ${res.written}, přeskočeno ${res.skipped}`, 'success')
      }
      await reloadPrehled()
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Generování směn selhalo', 'error')
    } finally {
      setBusy(false)
      setGeneratePlanDialog(null)
    }
  }

  if (authLoading || !user || gateOk === null) {
    return (
      <div className="app app--att-leader">
        <Header title={isHr ? 'Docházka — Personalista' : 'Docházka — Vedoucí'} showLogo />
        <div style={{ padding: '2rem', color: '#fff' }}>Načítání…</div>
      </div>
    )
  }

  const employeeDisplayName = formatDisplayNameFromString(
    prehled?.employee_name || selectedEmployee?.name || '',
  )

  const headerLine = [
    `Přehled docházky ${monthTitle(year, month)}`,
    employeeDisplayName || '—',
    prehled?.personal_number || null,
    prehled?.default_shift?.label || prehled?.default_shift?.name || prehled?.regime_name || null,
    prehled?.closed ? 'UZAVŘENÝ' : 'neuzavřeno',
  ]
    .filter(Boolean)
    .join(' - ')

  const defaultNormMins = defaultShiftNormMinutes(prehled)
  const isEmployeeClosed = (id: number) =>
    closedByEmployee[id] ?? (id === employeeId && !!prehled?.closed)

  const workspace = (
          <>
          {/* LEFT — employee list */}
          <aside className="att-legacy__emp">
            <div className="att-legacy__emp-head">
              <span>Příjmení</span>
              <span>Jméno</span>
            </div>
            {loadingList ? <p className="att-legacy__muted">Načítání…</p> : null}
            {!loadingList && employees.length === 0 ? (
              <p className="att-legacy__muted">Žádní zaměstnanci.</p>
            ) : null}
            <ul className="att-legacy__emp-list">
              {employees.map((e) => {
                const { surname, given } = formatPersonListColumns({ name: e.name })
                const active = e.employee_id === employeeId
                const closed = isEmployeeClosed(e.employee_id)
                return (
                  <li key={e.employee_id}>
                    <button
                      type="button"
                      className={[active ? 'is-active' : '', closed ? 'is-closed' : '']
                        .filter(Boolean)
                        .join(' ') || undefined}
                      onClick={() => selectEmployee(e.employee_id)}
                      title={closed ? 'Měsíc uzavřený' : 'Měsíc otevřený'}
                    >
                      <span className="att-legacy__emp-surname">{surname}</span>
                      <span className="att-legacy__emp-given">
                        {given}
                        {closed ? (
                          <em className="att-legacy__emp-closed-badge">UZAVŘENÝ</em>
                        ) : null}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </aside>

          {/* CENTER — month grid */}
          <section className="att-legacy__center">
            <header className="att-legacy__title">
              <h2>
                {headerLine}
                {prehled?.closed ? <em className="att-legacy__closed-badge">UZAVŘENÝ</em> : null}
              </h2>
              {rangeBounds ? (
                <div className="att-legacy__range">
                  Výběr: <strong>{rangeBounds.from}.–{rangeBounds.to}.</strong>
                  <span>pak klikněte na kód vpravo · Esc = jeden den</span>
                  <button type="button" onClick={() => setRangeAnchor(null)}>
                    Zrušit rozsah
                  </button>
                </div>
              ) : (
                <p className="att-legacy__hint">
                  Klik = den · Shift+klik = rozsah · pak kód nepřítomnosti / směny
                </p>
              )}
            </header>

            <div className="att-legacy__grid-wrap">
              {loadingPrehled && !prehled ? (
                <p className="att-legacy__muted">Načítání přehledu…</p>
              ) : null}
              <table className="att-legacy__grid">
                <thead>
                  <tr>
                    <th>Den</th>
                    <th>Dat.</th>
                    <th>Kód</th>
                    <th>Odprac.</th>
                    <th>Neodprac.</th>
                    <th>Rozdíl</th>
                    <th>Norma</th>
                  </tr>
                </thead>
                <tbody>
                  {(prehled?.days ?? []).map((day) => {
                    const active = selectedDay === day.day
                    const inRange =
                      rangeBounds != null &&
                      day.day >= rangeBounds.from &&
                      day.day <= rangeBounds.to
                    const code = dayCodeLabel(day)
                    const diff = day.minutes - day.norm_minutes
                    const neodprac = dayNeodpracLabel(day, defaultNormMins)
                    const odprac = dayOdpracLabel(day)
                    const rozdil = dayRozdilLabel(day)
                    const norma = dayNormaLabel(day)
                    return (
                      <tr
                        key={day.day_date}
                        className={[
                          day.weekend ? 'is-weekend' : '',
                          active ? 'is-active' : '',
                          inRange ? 'is-range' : '',
                          day.open ? 'is-open' : '',
                          day.absence ? 'is-absence' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={(e) => onDayClick(day, e)}
                      >
                        <td className="att-legacy__col-den">{day.weekday_short}</td>
                        <td className="att-legacy__col-dat">
                          <span>{String(day.day).padStart(2, '0')}</span>
                        </td>
                        <td className="att-legacy__col-kod" title={day.absence?.name || day.shift?.name || ''}>
                          {code || (day.weekend ? '' : '·')}
                        </td>
                        <td className="att-legacy__col-odprac">{odprac}</td>
                        <td className="att-legacy__col-neodprac">{neodprac}</td>
                        <td
                          className={
                            diff < 0
                              ? 'att-legacy__col-diff is-neg'
                              : diff > 0
                                ? 'att-legacy__col-diff is-pos'
                                : 'att-legacy__col-diff'
                          }
                        >
                          {rozdil}
                        </td>
                        <td className="att-legacy__col-norm">{norma}</td>
                      </tr>
                    )
                  })}
                </tbody>
                {prehled ? (
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Σ</td>
                      <td>{prehled.soucet.odpracovano_label}</td>
                      <td />
                      <td
                        className={
                          prehled.soucet.saldo_minutes < 0
                            ? 'is-neg'
                            : prehled.soucet.saldo_minutes > 0
                              ? 'is-pos'
                              : undefined
                        }
                      >
                        {prehled.soucet.saldo_label}
                      </td>
                      <td>{prehled.soucet.norma_label}</td>
                    </tr>
                    <tr className="att-legacy__grid-subfoot">
                      <td colSpan={7}>
                        Norma (Σ) {prehled.soucet.norma_label}
                        {prehled.soucet.stravenky_count != null
                          ? ` · Stravenky: ${prehled.soucet.stravenky_count}×`
                          : ''}
                        {prehled.banka
                          ? ` · Banka: ${prehled.banka.closing_label}${prehled.banka.posted ? '' : ' (odhad)'}`
                          : ''}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </section>

          {/* RIGHT — codes, nav, punch log */}
          <aside className="att-legacy__right">
            <div className="att-legacy__tabs">
              {(
                [
                  ['nepritomnosti', 'Důvody nepřítomnosti'],
                  ['smeny', 'Směny'],
                  ['soucet', 'Součet'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={rightTab === id ? 'is-active' : undefined}
                  onClick={() => setRightTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="att-legacy__tab-body">
              {rightTab === 'nepritomnosti' ? (
                <>
                  <p className="att-legacy__muted att-legacy__assign-hint">
                    {selectedDays.length === 0
                      ? 'Nejdřív vyberte den (nebo Shift+rozsah), pak kód.'
                      : selectedDays.length === 1
                        ? `Den ${selectedDays[0].day}. — klikněte na kód.`
                        : `${selectedDays.length} dnů — klikněte na kód.`}
                  </p>
                  <div className="att-legacy__codes">
                    {(prehled?.catalog.absence_reasons ?? []).map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        title={a.name}
                        disabled={busy || !!prehled?.closed || selectedDays.length === 0}
                        onClick={() =>
                          void applyToSelectedDays({
                            kind: 'absence',
                            id: a.id,
                            label: a.label || a.code || a.name,
                          })
                        }
                      >
                        <i style={{ background: codeSwatch(a.id) }} aria-hidden />
                        <strong>{a.label || a.code || '?'}</strong>
                        <span className="att-legacy__code-name">{a.name}</span>
                      </button>
                    ))}
                    {(prehled?.catalog.absence_reasons ?? []).length === 0 ? (
                      <p className="att-legacy__muted">Žádné důvody nepřítomnosti.</p>
                    ) : null}
                  </div>
                </>
              ) : null}

              {rightTab === 'smeny' ? (
                <>
                  <p className="att-legacy__muted att-legacy__assign-hint">
                    {selectedDays.length === 0
                      ? 'Nejdřív vyberte pracovní den, pak směnu.'
                      : selectedDays.length === 1
                        ? `Den ${selectedDays[0].day}. — klikněte na směnu.`
                        : `${selectedDays.length} dnů — klikněte na směnu.`}
                  </p>
                  <div className="att-legacy__codes">
                    {(prehled?.catalog.shifts ?? []).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        title={`${s.name}${s.time_from && s.time_to ? ` (${s.time_from}–${s.time_to})` : ''}`}
                        disabled={busy || !!prehled?.closed || selectedDays.length === 0}
                        onClick={() =>
                          void applyToSelectedDays({
                            kind: 'shift',
                            id: s.id,
                            label: s.label || s.name.slice(0, 4),
                          })
                        }
                      >
                        <i style={{ background: codeSwatch(s.id + 17) }} aria-hidden />
                        <strong>{s.label || s.name.slice(0, 4)}</strong>
                        <span className="att-legacy__code-name">
                          {s.name}
                          {s.time_from && s.time_to ? ` · ${s.time_from}–${s.time_to}` : ''}
                        </span>
                      </button>
                    ))}
                    {(prehled?.catalog.shifts ?? []).length === 0 ? (
                      <p className="att-legacy__muted">Žádné směny.</p>
                    ) : null}
                  </div>
                </>
              ) : null}

              {rightTab === 'soucet' && prehled ? (
                <dl className="att-legacy__soucet">
                  <div>
                    <dt>Norma</dt>
                    <dd>{prehled.soucet.norma_label}</dd>
                  </div>
                  <div>
                    <dt>Odpracováno</dt>
                    <dd>{prehled.soucet.odpracovano_label}</dd>
                  </div>
                  <div>
                    <dt>Saldo</dt>
                    <dd
                      className={
                        prehled.soucet.saldo_minutes < 0
                          ? 'is-neg'
                          : prehled.soucet.saldo_minutes > 0
                            ? 'is-pos'
                            : undefined
                      }
                    >
                      {prehled.soucet.saldo_label}
                    </dd>
                  </div>
                  {prehled.soucet.stravenky_count != null ? (
                    <div>
                      <dt>Stravenky</dt>
                      <dd>{prehled.soucet.stravenky_count}×</dd>
                    </div>
                  ) : null}
                  {prehled.banka ? (
                    <>
                      <div>
                        <dt>Banka začátek</dt>
                        <dd>{prehled.banka.opening_label}</dd>
                      </div>
                      <div>
                        <dt>Banka konec{prehled.banka.posted ? '' : ' (odhad)'}</dt>
                        <dd>{prehled.banka.closing_label}</dd>
                      </div>
                    </>
                  ) : null}
                  {isHr && employeeId != null ? (
                    <div className="att-legacy__soucet-actions">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setBankDialog({ delta: '0', note: '' })}
                      >
                        Upravit banku
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          void (async () => {
                            setBusy(true)
                            try {
                              await downloadAttendancePayrollCsv(year, month)
                            } catch (e: unknown) {
                              showAlert(e instanceof Error ? e.message : 'Export selhal', 'error')
                            } finally {
                              setBusy(false)
                            }
                          })()
                        }}
                      >
                        Export CSV
                      </button>
                    </div>
                  ) : null}
                </dl>
              ) : null}
            </div>

            <div className="att-legacy__monthbar">
              <button type="button" disabled={busy} onClick={() => shiftYear(-1)} title="−1 rok">
                ≪
              </button>
              <button type="button" disabled={busy} onClick={() => shiftMonth(-1)} title="−1 měsíc">
                ‹
              </button>
              <strong className="att-legacy__month-value">{monthTitle(year, month)}</strong>
              <button type="button" disabled={busy} onClick={() => shiftMonth(1)} title="+1 měsíc">
                ›
              </button>
              <button type="button" disabled={busy} onClick={() => shiftYear(1)} title="+1 rok">
                ≫
              </button>
            </div>

            <div className="att-legacy__actions">
              <button
                type="button"
                disabled={busy || employeeId == null}
                onClick={() => {
                  if (employeeId == null) return
                  void (async () => {
                    setBusy(true)
                    try {
                      await downloadAttendancePrehledPdf(employeeId, year, month)
                    } catch (e: unknown) {
                      showAlert(e instanceof Error ? e.message : 'PDF selhalo', 'error')
                    } finally {
                      setBusy(false)
                    }
                  })()
                }}
              >
                Tisk
              </button>
              <button
                type="button"
                disabled={busy || !!prehled?.closed || selectedDays.length === 0}
                onClick={() => void clearSelectedCodes()}
                title="Smaže směnu nebo nepřítomnost u vybraného dne / rozsahu"
              >
                Smazat kód dne
              </button>
              {prehled?.closed ? (
                isHr ? (
                  <button
                    type="button"
                    disabled={busy || employeeId == null}
                    onClick={() => {
                      if (employeeId == null) return
                      void (async () => {
                        setBusy(true)
                        try {
                          await attendancePersonalistaPeriodAction('open', {
                            employee_id: employeeId,
                            year,
                            month,
                          })
                          showAlert('Období odemčeno', 'success')
                          await reloadPrehled()
                        } catch (e: unknown) {
                          showAlert(e instanceof Error ? e.message : 'Odemčení selhalo', 'error')
                        } finally {
                          setBusy(false)
                        }
                      })()
                    }}
                  >
                    Odemknout
                  </button>
                ) : (
                  <span className="att-legacy__locked">Uzavřeno</span>
                )
              ) : (
                <button
                  type="button"
                  disabled={busy || employeeId == null}
                  onClick={() => {
                    if (employeeId == null) return
                    setCloseChecklist({ scope: 'one', items: buildCloseChecklist() })
                  }}
                >
                  Uzavřít
                </button>
              )}
              {isHr ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setCloseChecklist({
                      scope: 'all',
                      items: [
                        {
                          level: 'warn',
                          text: 'Uzavře období u všech zaměstnanců.',
                        },
                      ],
                    })
                  }}
                >
                  Uzavřít všechny
                </button>
              ) : null}
              <button
                type="button"
                disabled={busy || employeeId == null || !!prehled?.closed}
                title="Vyplní směny z rozvrhu"
                onClick={() => {
                  if (employeeId == null) return
                  setGeneratePlanDialog({ overwrite: false })
                }}
              >
                Generovat
              </button>
              <button
                type="button"
                disabled={busy || employeeId == null || !!prehled?.closed}
                onClick={() => {
                  if (employeeId == null) return
                  setConfirmDialog({ kind: 'copy_month' })
                }}
              >
                Kopírovat
              </button>
            </div>

            <div className="att-legacy__punch-tools">
              <button type="button" disabled={busy || !!prehled?.closed} onClick={() => openCreate('arrival')}>
                + Příchod
              </button>
              <button type="button" disabled={busy || !!prehled?.closed} onClick={() => openCreate('departure')}>
                + Odchod
              </button>
              <button
                type="button"
                disabled={busy || !!prehled?.closed}
                onClick={() => openEditPunch(selectedPunch)}
              >
                Upravit
              </button>
              <button type="button" disabled={busy || !!prehled?.closed} onClick={() => void removeSelected()}>
                Smazat
              </button>
            </div>

            {prehled?.closed ? (
              <p className="att-legacy__warn">Období je uzavřeno — úpravy jsou zablokované.</p>
            ) : null}
            {prehled && !prehled.has_user_account ? (
              <p className="att-legacy__warn">
                Bez intranet účtu — události nelze zakládat (kódy směn/nepřítomností ano).
              </p>
            ) : null}

            {edit ? (
              <div className="att-legacy__form">
                <label>
                  Typ
                  <select
                    value={edit.type}
                    onChange={(e) =>
                      setEdit({ ...edit, type: e.target.value as AttendancePunchType })
                    }
                  >
                    {PUNCH_TYPES.map((t) => (
                      <option key={t.type} value={t.type}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Datum a čas
                  <input
                    type="datetime-local"
                    value={edit.punchedAtLocal}
                    onChange={(e) => setEdit({ ...edit, punchedAtLocal: e.target.value })}
                  />
                </label>
                <div className="att-legacy__form-actions">
                  <button type="button" disabled={busy} onClick={() => void saveEdit()}>
                    {busy ? 'Ukládám…' : 'Uložit'}
                  </button>
                  <button type="button" disabled={busy} onClick={() => setEdit(null)}>
                    Zrušit
                  </button>
                </div>
              </div>
            ) : null}

            <div className="att-legacy__log-head">
              <span>Den</span>
              <span>Čas</span>
              <span>Důvod</span>
              <span>Doba</span>
              <span>Kód</span>
            </div>
            <ul className="att-legacy__log">
              {punchLogRows.length === 0 ? (
                <li className="att-legacy__muted">Žádné události v měsíci.</li>
              ) : (
                punchLogRows.map((row) => (
                  <li
                    key={row.key}
                    className={row.dayStart ? 'att-legacy__log-daysep' : undefined}
                  >
                    <button
                      type="button"
                      className={[
                        'att-legacy__log-row',
                        row.auto ? 'is-auto' : '',
                        row.punch && selectedPunchId === row.punch.id ? 'is-selected' : '',
                        row.day === selectedDay ? 'is-day' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        if (row.punch) setSelectedPunchId(row.punch.id)
                        if (row.day != null) {
                          setRangeAnchor(null)
                          setSelectedDay(row.day)
                        }
                      }}
                      onDoubleClick={() => {
                        if (row.punch) openEditPunch(row.punch)
                      }}
                      title={row.auto ? 'Automaticky ze směny (nelze upravit)' : undefined}
                    >
                      <span>{row.day != null ? String(row.day).padStart(2, '0') : '—'}</span>
                      <span>{formatPunchClock(row.punchedAt)}</span>
                      <span
                        className="att-legacy__punch-reason"
                        style={
                          row.color
                            ? { color: row.color, fontWeight: 700 }
                            : undefined
                        }
                      >
                        {row.label}
                      </span>
                      <span>{row.duration}</span>
                      <span>{row.shiftLabel}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
            {selectedDayRow ? (
              <p className="att-legacy__day-note">
                Vybraný den: {selectedDayRow.day}. · {selectedDayRow.hours_label}
                {selectedDayRow.open ? ' (otevřeno)' : ''}
              </p>
            ) : null}
          </aside>
          </>
  )

  return (
    <div className={`app${!isHr ? ' app--att-leader' : ' app--att-hr'}`}>
      <Header
        leftButtons={
          <button type="button" className="back-button" onClick={() => navigate('/attendance')}>
            ← Zpět na výběr role
          </button>
        }
        title={isHr ? 'Docházka — Personalista' : 'Docházka — Vedoucí'}
        rightContent={
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>{user.name}</span>
        }
        showLogo
      />

      {isHr ? (
        <div className="attendance-root">
          <aside className="attendance-sidebar">
            <div className="attendance-status">
              <div className="attendance-status__row">
                <span className="attendance-status__label">Personalista</span>
              </div>
              <div className="attendance-status__hint">Měsíční přehled a správa docházky.</div>
            </div>
            <nav className="attendance-nav" aria-label="Personalista — sekce">
              <button
                type="button"
                className="attendance-nav__item active"
                onClick={() => navigate('/attendance/personalista')}
              >
                Měsíční přehled
              </button>
              {ATT_HR_SECTIONS.map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className="attendance-nav__item"
                  onClick={() => navigate(`/attendance/personalista/${k}`)}
                >
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          <div className="attendance-content">
            <div className="attendance-content__head">
              <h2>Měsíční přehled</h2>
              <div className="attendance-content__period">
                <button type="button" disabled={busy} onClick={() => shiftMonth(-1)}>
                  ‹
                </button>
                <strong>{monthTitle(year, month)}</strong>
                <button type="button" disabled={busy} onClick={() => shiftMonth(1)}>
                  ›
                </button>
              </div>
            </div>
            <p className="attendance-muted" style={{ marginTop: 0, marginBottom: '0.85rem' }}>
              Kliknutím na zaměstnance otevřete přehled docházky.
            </p>
            <div className="att-overview-filters">
              <label>
                Hledat
                <input
                  type="search"
                  value={overviewSearch}
                  placeholder="jméno / os. číslo"
                  onChange={(e) => setOverviewSearch(e.target.value)}
                />
              </label>
              <label>
                Stav
                <select
                  value={overviewStatus}
                  onChange={(e) => setOverviewStatus(e.target.value as 'all' | 'open' | 'closed')}
                >
                  <option value="all">Vše</option>
                  <option value="open">Otevřeno</option>
                  <option value="closed">Uzavřeno</option>
                </select>
              </label>
              <span className="attendance-muted">
                {filteredHrOverview.length}/{hrOverview.length}
              </span>
            </div>
            {loadingHrOverview ? (
              <p className="attendance-muted">Načítání přehledu…</p>
            ) : (
              <div className="att-cat__wrap">
                <table className="att-cat__table att-cat__table--overview">
                  <thead>
                    <tr>
                      <th className="att-cat__col-name att-cat__col-name--person">Zaměstnanec</th>
                      <th className="att-cat__col-narrow">Os. č.</th>
                      <th className="att-cat__col-narrow">Hodiny</th>
                      <th className="att-cat__col-narrow">Událostí</th>
                      <th className="att-cat__col-narrow">Otevřené</th>
                      <th className="att-cat__col-fill">Stav</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHrOverview.map((e) => (
                      <tr
                        key={e.employee_id}
                        className={
                          detailOpen && employeeId === e.employee_id
                            ? 'att-legacy__hr-row is-selected'
                            : 'att-legacy__hr-row'
                        }
                        onClick={() => openEmployeeDetail(e.employee_id)}
                      >
                        <td
                          className="att-cat__col-name att-cat__col-name--person"
                          title={formatDisplayNameFromString(e.name)}
                        >
                          {formatDisplayNameFromString(e.name)}
                        </td>
                        <td className="att-cat__col-narrow">{e.personal_number || '—'}</td>
                        <td className="att-cat__col-narrow">{e.month_hours_label}</td>
                        <td className="att-cat__col-narrow">{e.punches_count}</td>
                        <td className="att-cat__col-narrow">{e.open_days}</td>
                        <td className="att-cat__col-narrow">{e.closed ? 'Uzavřeno' : 'Otevřeno'}</td>
                      </tr>
                    ))}
                    {filteredHrOverview.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="att-cat__empty">
                          {hrOverview.length === 0 ? 'Žádná data.' : 'Žádný řádek neodpovídá filtru.'}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="att-prehled">
          <section className="att-legacy att-legacy--page" aria-label="Přehled docházky">
            <div className="att-legacy__workspace">{workspace}</div>
          </section>
        </div>
      )}

      {isHr && detailOpen ? (
        <div
          className="att-prehled__modal-overlay att-legacy__detail-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Přehled docházky zaměstnance"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeEmployeeDetail()
          }}
        >
          <div className="att-legacy__detail-modal">
            <div className="att-legacy__detail-modal-head">
              <strong>{headerLine}</strong>
              <button type="button" onClick={closeEmployeeDetail} aria-label="Zavřít">
                ✕
              </button>
            </div>
            <section className="att-legacy att-legacy--modal" aria-label="Přehled docházky">
              <div className="att-legacy__workspace">{workspace}</div>
            </section>
          </div>
        </div>
      ) : null}

      {closeChecklist ? (
        <div className="att-prehled__modal-overlay" role="dialog" aria-modal="true">
          <div className="att-prehled__dialog">
            <h3>Kontrola před uzávěrkou</h3>
            <p>
              {closeChecklist.scope === 'all'
                ? `Uzavřít období ${monthTitle(year, month)} u všech zaměstnanců?`
                : `Uzavřít období ${monthTitle(year, month)}? Po uzavření nelze upravovat kódy ani události.`}
            </p>
            {closeChecklist.items.length === 0 ? (
              <p className="att-legacy__muted">Kontrola nenašla zjevné problémy.</p>
            ) : (
              <ul className="att-prehled__checklist">
                {closeChecklist.items.map((it, i) => (
                  <li key={i} className={it.level === 'warn' ? 'is-warn' : 'is-info'}>
                    {it.text}
                  </li>
                ))}
              </ul>
            )}
            <div className="att-prehled__dialog-actions">
              <button type="button" disabled={busy} onClick={() => setCloseChecklist(null)}>
                Zrušit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void runClosePeriod(closeChecklist.scope)}
              >
                {busy ? 'Uzavírám…' : 'Uzavřít'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {generatePlanDialog ? (
        <div className="att-prehled__modal-overlay" role="dialog" aria-modal="true">
          <div className="att-prehled__dialog">
            <h3>Vygenerovat směny</h3>
            <p>
              Doplní směny z rozvrhu / směny zaměstnance. Nepřítomnosti zůstanou. Prázdné dny se
              doplní směnami.
            </p>
            <label className="att-prehled__dialog-check">
              <input
                type="checkbox"
                checked={generatePlanDialog.overwrite}
                onChange={(e) => setGeneratePlanDialog({ overwrite: e.target.checked })}
              />
              Přepsat i dny, které už mají směnu
            </label>
            <div className="att-prehled__dialog-actions">
              <button type="button" disabled={busy} onClick={() => setGeneratePlanDialog(null)}>
                Zrušit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void runGeneratePlan(generatePlanDialog.overwrite)}
              >
                {busy ? 'Generuji…' : 'Vygenerovat'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {bankDialog && employeeId != null ? (
        <div className="att-prehled__modal-overlay" role="dialog" aria-modal="true">
          <div className="att-prehled__dialog">
            <h3>Úprava banky hodin</h3>
            <label>
              Delta v minutách (např. 60 nebo -30)
              <input
                type="number"
                value={bankDialog.delta}
                onChange={(e) => setBankDialog({ ...bankDialog, delta: e.target.value })}
              />
            </label>
            <label>
              Poznámka (volitelné)
              <input
                type="text"
                value={bankDialog.note}
                onChange={(e) => setBankDialog({ ...bankDialog, note: e.target.value })}
              />
            </label>
            <div className="att-prehled__dialog-actions">
              <button type="button" disabled={busy} onClick={() => setBankDialog(null)}>
                Zrušit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  const delta = parseInt(bankDialog.delta, 10)
                  if (!Number.isFinite(delta) || delta === 0) {
                    showAlert('Zadejte nenulové minuty.', 'info')
                    return
                  }
                  void (async () => {
                    setBusy(true)
                    try {
                      await attendancePersonalistaBankAdjust({
                        employee_id: employeeId,
                        delta_minutes: delta,
                        note: bankDialog.note,
                      })
                      showAlert('Banka upravena', 'success')
                      await reloadPrehled()
                      setBankDialog(null)
                    } catch (e: unknown) {
                      showAlert(e instanceof Error ? e.message : 'Úprava banky selhala', 'error')
                    } finally {
                      setBusy(false)
                    }
                  })()
                }}
              >
                {busy ? 'Ukládám…' : 'Uložit'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDialog ? (
        <div className="att-prehled__modal-overlay" role="dialog" aria-modal="true">
          <div className="att-prehled__dialog">
            <h3>
              {confirmDialog.kind === 'delete_punch'
                ? 'Smazat událost'
                : confirmDialog.kind === 'copy_month'
                  ? 'Kopírovat kódy'
                  : 'Potvrzení'}
            </h3>
            <p>
              {confirmDialog.kind === 'delete_punch'
                ? `Smazat událost ${confirmDialog.label}?`
                : confirmDialog.kind === 'copy_month'
                  ? `Zkopírovat kódy dnů z ${month === 1 ? 12 : month - 1}/${month === 1 ? year - 1 : year} do ${month}/${year}? Existující dny se nepřepíší.`
                  : ''}
            </p>
            <div className="att-prehled__dialog-actions">
              <button type="button" disabled={busy} onClick={() => setConfirmDialog(null)}>
                Zrušit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (confirmDialog.kind === 'delete_punch') {
                    void confirmDeletePunch()
                    return
                  }
                  if (confirmDialog.kind === 'copy_month' && employeeId != null) {
                    const prevMonth = month === 1 ? 12 : month - 1
                    const prevYear = month === 1 ? year - 1 : year
                    void (async () => {
                      setBusy(true)
                      try {
                        const input = {
                          employee_id: employeeId,
                          from_year: prevYear,
                          from_month: prevMonth,
                          to_year: year,
                          to_month: month,
                          overwrite: false,
                        }
                        const res = isHr
                          ? await attendancePersonalistaCopyMonthPlan(input)
                          : await attendanceLeaderCopyMonthPlan(input)
                        showAlert(
                          `Zkopírováno ${res.copied} dnů (${res.from} → ${res.to})`,
                          'success',
                        )
                        await reloadPrehled()
                      } catch (e: unknown) {
                        showAlert(e instanceof Error ? e.message : 'Kopírování selhalo', 'error')
                      } finally {
                        setBusy(false)
                        setConfirmDialog(null)
                      }
                    })()
                  }
                }}
              >
                {busy ? 'Provádím…' : 'Potvrdit'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
